import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { KolPostingLinksTable } from "@/components/kol/KolPostingLinksTable";
import KolDbTable from "@/components/kol/KolDbTable";
import KolPriceListTable from "@/components/kol/KolPriceListTable";
import TopicsBusinessUnitsTable from "@/components/kol/TopicsBusinessUnitsTable";
import { DataImportPage } from "@/components/kol/DataImportPage";
import { useTheme } from "@/contexts/ThemeContext";
import { Link, Database, DollarSign, Hash, Users, Target } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useKolPostingLinksFilters } from "@/hooks/useKolPostingLinksFilters";

const ITEMS_PER_PAGE = 50;

export const KolTracker = () => {
  const [searchParams] = useSearchParams();
  
  // Initialize activeTab from URL parameter or default to "kol-tracker-table"
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'data-import-v2') return 'data-import';
    if (tabParam === 'kol-db-table') return 'kol-db-table';
    if (tabParam === 'kol-price-list-table') return 'kol-price-list-table';
    if (tabParam === 'content-id-table') return 'content-id-table';
    return 'kol-tracker-table';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [isKolDbTableVisible, setIsKolDbTableVisible] = useState(getInitialTab() === 'kol-db-table');
  const [isKolTrackerTableVisible, setIsKolTrackerTableVisible] = useState(getInitialTab() === 'kol-tracker-table');
  const [isKolPriceListTableVisible, setIsKolPriceListTableVisible] = useState(getInitialTab() === 'kol-price-list-table');
  const [isTopicsBusinessUnitsTableVisible, setIsTopicsBusinessUnitsTableVisible] = useState(getInitialTab() === 'content-id-table');
  const [isDataImportVisible, setIsDataImportVisible] = useState(getInitialTab() === 'data-import');
  
  // Date filter state - default to current year (2025)
  const [dateFilterStartDate, setDateFilterStartDate] = useState<Date | null>(new Date('2025-01-01'));
  const [dateFilterEndDate, setDateFilterEndDate] = useState<Date | null>(new Date('2025-12-31'));
  
  // Tracker state with search
  const [kolData, setKolData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [trackerPage, setTrackerPage] = useState(1);
  const [trackerPageSize, setTrackerPageSize] = useState(50);
  const [trackerTotalCount, setTrackerTotalCount] = useState(0);
  const [trackerHasError, setTrackerHasError] = useState(false);
  const [trackerSearchTerm, setTrackerSearchTerm] = useState("");
  const [sortByCreatedTime, setSortByCreatedTime] = useState(true);
  const [dbSortColumn, setDbSortColumn] = useState<string | null>(null);
  const [dbSortDirection, setDbSortDirection] = useState<'asc' | 'desc' | null>(null);
  
  // Price state with search - UPDATED to fetch ALL records with content types
  const [kolPriceData, setKolPriceData] = useState<any[]>([]);
  const [isKolPriceLoading, setIsKolPriceLoading] = useState(true);
  const [priceHasError, setPriceHasError] = useState(false);
  const [priceSearchTerm, setPriceSearchTerm] = useState("");
  
  const [isTopicsBusinessUnitsLoading, setIsTopicsBusinessUnitsLoading] = useState(true);
  
  const { hasFullAccess, hasBuAccess, userName, userBu } = useAuth();
  const { toast } = useToast();
  const { theme } = useTheme();

  // Initialize filters hook at page level
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    applyFilters,
    isLoading: isFiltersLoading
  } = useKolPostingLinksFilters();

  // Helper function to get user's actual name from kol_pic table
  const getUserActualName = useCallback(async (emailUsername: string) => {
    try {
      const { data, error } = await supabase
        .from('kol_pic')
        .select('name')
        .or(`email.ilike.%${emailUsername}%,name.ilike.%${emailUsername}%`)
        .limit(1);
      
      if (error) {
        console.error("Error fetching user name:", error);
        return null;
      }
      
      return data && data.length > 0 ? data[0].name : null;
    } catch (error) {
      console.error("Error in getUserActualName:", error);
      return null;
    }
  }, []);

  // Enhanced fetch function with proper filter integration and sorting
  const fetchData = useCallback(async () => {
    if (!isKolTrackerTableVisible) return;
    
    try {
      setIsLoading(true);
      setTrackerHasError(false);
      console.log("Fetching KOL Tracker data with search:", trackerSearchTerm, "BU Access:", hasBuAccess, "Filters:", hasActiveFilters, "Sort by created:", sortByCreatedTime, "Page size:", trackerPageSize, "Date filter:", { dateFilterStartDate, dateFilterEndDate });
      console.log("🔍 KOL Tracker Query Debug - User:", userName, "hasFullAccess:", hasFullAccess, "hasBuAccess:", hasBuAccess);
      
      // For "Show All" option, don't use pagination
      const usesPagination = trackerPageSize !== -1;
      const itemsPerPage = usesPagination ? trackerPageSize : 1000000; // Use a very large number for "Show All"
      const offset = usesPagination ? (trackerPage - 1) * trackerPageSize : 0;
      
      // Build base query for count
      let countQuery = supabase
        .from('kol_tracker')
        .select('*', { count: 'exact', head: true });
      
      // Add date filter
      if (dateFilterStartDate) {
        countQuery = countQuery.gte('posting_date', dateFilterStartDate.toISOString().split('T')[0]);
      }
      if (dateFilterEndDate) {
        countQuery = countQuery.lte('posting_date', dateFilterEndDate.toISOString().split('T')[0]);
      }
      
      // Add search filters if search term exists
      if (trackerSearchTerm.trim()) {
        countQuery = countQuery.or(`name.ilike.%${trackerSearchTerm}%,channel.ilike.%${trackerSearchTerm}%,platform.ilike.%${trackerSearchTerm}%,type.ilike.%${trackerSearchTerm}%`);
      }
      
      // Apply filters to count query
      if (hasActiveFilters) {
        countQuery = applyFilters(countQuery);
      }
      
      // Get count first
      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error("Count error:", countError);
        throw countError;
      }
      
      console.log("Total count with filters:", count);
      setTrackerTotalCount(count || 0);

      // Build data query with same filters and proper sorting
      let dataQuery = supabase
        .from('kol_tracker')
        .select('*');
        
      // Only apply range if using pagination
      if (usesPagination) {
        dataQuery = dataQuery.range(offset, offset + itemsPerPage - 1);
      }
      
      // Add same date filter to data query
      if (dateFilterStartDate) {
        dataQuery = dataQuery.gte('posting_date', dateFilterStartDate.toISOString().split('T')[0]);
      }
      if (dateFilterEndDate) {
        dataQuery = dataQuery.lte('posting_date', dateFilterEndDate.toISOString().split('T')[0]);
      }
      
      // Apply sorting at database level
      if (dbSortColumn && dbSortDirection) {
        // Database-level column sorting with proper date handling
        dataQuery = dataQuery.order(dbSortColumn, { ascending: dbSortDirection === 'asc', nullsFirst: false });
      } else if (sortByCreatedTime) {
        dataQuery = dataQuery.order('created_at', { ascending: false });
      } else {
        dataQuery = dataQuery.order('posting_date', { ascending: false, nullsFirst: false });
      }
      
      // Add secondary sorting by platform to prioritize Twitter/X (only if not already sorting by platform)
      if (dbSortColumn !== 'platform') {
        dataQuery = dataQuery.order('platform', { ascending: true, nullsFirst: false });
      }
      
      // Apply same search filters to data query
      if (trackerSearchTerm.trim()) {
        dataQuery = dataQuery.or(`name.ilike.%${trackerSearchTerm}%,channel.ilike.%${trackerSearchTerm}%,platform.ilike.%${trackerSearchTerm}%,type.ilike.%${trackerSearchTerm}%`);
      }
      
      // Apply same filters to data query
      if (hasActiveFilters) {
        dataQuery = applyFilters(dataQuery);
      }
      
      const { data, error } = await dataQuery;
      if (error) {
        console.error("Data fetch error:", error);
        throw error;
      }

      console.log("Fetched filtered data length:", data?.length);

      // Don't apply additional client-side sorting when database sorting is active
      // The database sorting should be respected for posting_date
      setKolData(data || []);
    } catch (error: any) {
      console.error("Error in fetchData:", error);
      setTrackerHasError(true);
      toast({
        title: "Error",
        description: `Failed to load KOL Posting Links data: ${error.message || 'Unknown error'}`,
        variant: "destructive"
      });
      setKolData([]);
    } finally {
      setIsLoading(false);
    }
  }, [isKolTrackerTableVisible, trackerPage, trackerPageSize, trackerSearchTerm, toast, hasBuAccess, hasActiveFilters, applyFilters, sortByCreatedTime, dbSortColumn, dbSortDirection, dateFilterStartDate, dateFilterEndDate]);

  const fetchKolPriceData = useCallback(async () => {
    if (!isKolPriceListTableVisible) return;
    
    try {
      setIsKolPriceLoading(true);
      setPriceHasError(false);
      console.log("Fetching KOL Price data with RLS-based access control");
      
      setKolPriceData([]);
      
    } catch (error: any) {
      console.error("Error in fetchKolPriceData:", error);
      setPriceHasError(true);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to load price list data: ${errorMessage}`,
        variant: "destructive"
      });
      setKolPriceData([]);
    } finally {
      setIsKolPriceLoading(false);
    }
  }, [isKolPriceListTableVisible, priceSearchTerm, hasFullAccess, userName, toast]);

  const handleDataChange = useCallback(() => {
    if (isKolTrackerTableVisible) {
      fetchData();
    }
    if (isKolPriceListTableVisible) {
      fetchKolPriceData();
    }
  }, [isKolTrackerTableVisible, isKolPriceListTableVisible, fetchData, fetchKolPriceData]);

  const handleTrackerSearchChange = useCallback((term: string) => {
    setTrackerSearchTerm(term);
    setTrackerPage(1); // Reset to first page on search
  }, []);

  const handlePriceSearchChange = useCallback((term: string) => {
    setPriceSearchTerm(term);
    // No need to reset page since price list doesn't use pagination
  }, []);

  const handleTrackerSearch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handlePriceSearch = useCallback(() => {
    fetchKolPriceData();
  }, [fetchKolPriceData]);

  const handleTrackerPageChange = useCallback((newPage: number) => {
    console.log(`Tracker page change to: ${newPage}`);
    setTrackerPage(newPage);
  }, []);

  const handleTrackerPageSizeChange = useCallback((newPageSize: number) => {
    console.log(`Tracker page size change to: ${newPageSize}`);
    setTrackerPageSize(newPageSize);
    setTrackerPage(1); // Reset to first page when page size changes
  }, []);

  const handleSortChange = useCallback((newSortByCreatedTime: boolean) => {
    console.log('Sort change:', newSortByCreatedTime);
    setSortByCreatedTime(newSortByCreatedTime);
    setTrackerPage(1); // Reset to first page when sorting changes
  }, []);

  const handleDbSort = useCallback((column: string | null, direction: 'asc' | 'desc' | null) => {
    console.log('Database sort change:', { column, direction });
    setDbSortColumn(column);
    setDbSortDirection(direction);
    setTrackerPage(1); // Reset to first page when sorting changes
  }, []);

  // Date filter handlers
  const handleDateFilterReset = useCallback(() => {
    setDateFilterStartDate(new Date('2025-01-01'));
    setDateFilterEndDate(new Date('2025-12-31'));
    setTrackerPage(1);
  }, []);

  const handleStartDateChange = useCallback((date: Date | null) => {
    setDateFilterStartDate(date);
    setTrackerPage(1);
  }, []);

  const handleEndDateChange = useCallback((date: Date | null) => {
    setDateFilterEndDate(date);
    setTrackerPage(1);
  }, []);

  useEffect(() => {
    if (isKolTrackerTableVisible && hasFullAccess !== undefined && hasBuAccess !== undefined) {
      const debounceTimer = setTimeout(() => {
        fetchData();
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [isKolTrackerTableVisible, trackerPage, trackerPageSize, trackerSearchTerm, hasFullAccess, hasBuAccess, hasActiveFilters, filters, sortByCreatedTime, dbSortColumn, dbSortDirection, dateFilterStartDate, dateFilterEndDate]);
  
  useEffect(() => {
    if (isKolPriceListTableVisible && hasFullAccess !== undefined && hasBuAccess !== undefined) {
      const debounceTimer = setTimeout(() => {
        fetchKolPriceData();
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [isKolPriceListTableVisible, priceSearchTerm, hasFullAccess, hasBuAccess]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
            <Target className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">KOL TRACKER</h1>
            <p className="text-muted-foreground mt-1">
              Manage KOL relationships, track performance metrics, and organize campaign data
            </p>
          </div>
        </div>
      </div>
      
      {!hasFullAccess && !hasBuAccess && (
        <div className="text-sm italic text-muted-foreground">
          Limited access mode: You can only view and edit your own KOL records
        </div>
      )}
      
      {hasBuAccess && (
        <div className="text-sm italic text-muted-foreground">
          BU access mode: You can view and edit KOL records based on your business unit permissions
        </div>
      )}
      
      <Tabs 
        defaultValue="kol-tracker-table" 
        className="w-full"
        value={activeTab}
        onValueChange={(val) => {
          setActiveTab(val);
          setIsKolTrackerTableVisible(val === "kol-tracker-table");
          setIsKolDbTableVisible(val === "kol-db-table");
          setIsKolPriceListTableVisible(val === "kol-price-list-table");
          setIsTopicsBusinessUnitsTableVisible(val === "content-id-table");
          setIsDataImportVisible(val === "data-import");
        }}
      >
        <TabsList className="border border-border bg-transparent">
          <TabsTrigger 
            value="kol-tracker-table"
            className="tabs-trigger"
          >
            <Link className="h-4 w-4" />
            KOL Posting Links
          </TabsTrigger>
          {/* Hide KOL Dictionary tab for BU Access users */}
          {!hasBuAccess && (
            <TabsTrigger 
              value="kol-db-table"
              className="tabs-trigger"
            >
              <Database className="h-4 w-4" />
              KOL Dictionary
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="kol-price-list-table"
            className="tabs-trigger"
          >
            <DollarSign className="h-4 w-4" />
            KOL Price List Table
          </TabsTrigger>
          {/* Show Content ID tab for Full Access and Limited Access users, hide only from BU Access */}
          {!hasBuAccess && (
            <TabsTrigger 
              value="content-id-table"
              className="tabs-trigger"
            >
              <Hash className="h-4 w-4" />
              Content ID
            </TabsTrigger>
          )}
          {/* Show Data Import V2 tab for Full Access and Limited Access users, hide only from BU Access */}
          {!hasBuAccess && (
            <TabsTrigger 
              value="data-import"
              className="tabs-trigger"
            >
              <Users className="h-4 w-4" />
              Data Import V2
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="kol-tracker-table" className="space-y-4">
          {isKolTrackerTableVisible && (
            <KolPostingLinksTable 
              data={kolData} 
              isLoading={isLoading} 
              onDataChange={handleDataChange}
              hasFullAccess={hasFullAccess}
              currentUserName={userName || ''}
              totalCount={trackerTotalCount}
              currentPage={trackerPage}
              onPageChange={handleTrackerPageChange}
              pageSize={trackerPageSize}
              onPageSizeChange={handleTrackerPageSizeChange}
              searchTerm={trackerSearchTerm}
              onSearchChange={handleTrackerSearchChange}
              onSearch={handleTrackerSearch}
              filters={filters}
              onFilterChange={updateFilter}
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
              sortByCreatedTime={sortByCreatedTime}
              onSortChange={handleSortChange}
              dbSortColumn={dbSortColumn}
              dbSortDirection={dbSortDirection}
              onDbSort={handleDbSort}
            />
          )}
        </TabsContent>
        
        {!hasBuAccess && (
          <TabsContent value="kol-db-table" className="space-y-4">
            {isKolDbTableVisible && (
              <KolDbTable onDataChange={handleDataChange} />
            )}
          </TabsContent>
        )}
        
        <TabsContent value="kol-price-list-table" className="space-y-4">
          {isKolPriceListTableVisible && (
            <KolPriceListTable 
              isLoading={isKolPriceLoading} 
              onDataChange={handleDataChange} 
              hasError={priceHasError}
              searchTerm={priceSearchTerm}
              onSearchChange={handlePriceSearchChange}
              hasFullAccess={hasFullAccess}
              currentUserName={userName || ''}
            />
          )}
        </TabsContent>
        
        {!hasBuAccess && (
          <TabsContent value="content-id-table" className="space-y-4">
            {isTopicsBusinessUnitsTableVisible && (
              <TopicsBusinessUnitsTable 
                isLoading={isTopicsBusinessUnitsLoading} 
                onDataChange={handleDataChange}
              />
            )}
          </TabsContent>
        )}
        
        {!hasBuAccess && (
          <TabsContent value="data-import" className="p-0">
            {isDataImportVisible && (
              <DataImportPage onDataImported={handleDataChange} />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default KolTracker;
